import { create } from 'zustand'
import type { StateManager, ActionFlowEngine } from '@cogo/runtime-v2'
import type { Flow } from '@cogo/types-v2'
import { IFRAME_EVENTS, type IframeMessage, type FlowExecutedPayload, type EventFiredPayload, type StateSnapshotPayload, type NetworkRequestPayload } from '@cogo/iframe-protocol'

/** Shape exposed by AppV2.onReady — subset of full AppRuntime */
export type V2RuntimeCtx = { stateManager: StateManager; actionEngine: ActionFlowEngine }

export type DevToolsTab =
  | 'state'
  | 'events'
  | 'flows'
  | 'element'
  | 'performance'
  | 'validation'
  | 'tests'
  | 'tools'
  | 'kg'
  | 'kg-validate'
export type DevToolsBottomTab = 'console' | 'state' | 'network' | 'spec-compliance' | 'kg-validate'

/**
 * Known `CogoKnowledgeRepository` methods captured by the KG Inspector proxy.
 * Used as a filter vocabulary in the `KgInspectorTab` UI. Kept as a string
 * literal union + array to avoid a runtime dep on `@cogo/kg-repository` from
 * the store layer (the proxy supplies these names at call time).
 */
export type KgMethodName =
  | 'findExamples'
  | 'getDesignSystem'
  | 'listDesignSystems'
  | 'getSemanticTypes'
  | 'getActions'
  | 'getEvents'
  | 'validate'
  | 'recordObservation'
  | 'registerExample'
  | 'upsertDesignSystem'
  | 'loadJsonLd'
  | 'dropGraph'
  | 'ready'
  | (string & {})

/** One record per observed `repo.<method>(...)` call, appended to a ring buffer. */
export interface KgCallRecord {
  id: string
  timestamp: number
  method: KgMethodName
  durationMs: number
  source: 'local' | 'server' | 'cache' | 'unknown'
  cacheHit?: boolean
  /** True when the underlying promise rejected. `durationMs` reflects time-to-throw. */
  error?: string
}

export type ConsoleLevel = 'info' | 'warn' | 'error'
export interface ConsoleEntry {
  id: string
  timestamp: number
  level: ConsoleLevel
  source: 'flow' | 'event' | 'state' | 'error' | 'network'
  message: string
  detail?: unknown
}

export interface EventLogEntry {
  id: string
  timestamp: number
  elementId: string
  eventType: string
  flowName?: string
  payload?: unknown
}

export interface FlowLogEntry {
  id: string
  timestamp: number
  flowName: string
  duration: number
  stepCount: number
  success: boolean
  error?: string
}

export interface NetworkEntry {
  id: string
  timestamp: number
  method: string
  url: string
  status: number
  duration: number
  requestBody?: unknown
  responseBody?: unknown
  error?: string
}

export interface DevToolsState {
  // Runtime reference (v2 onReady context)
  runtime: V2RuntimeCtx | null
  setRuntime: (runtime: V2RuntimeCtx | null) => void

  // Active tab
  activeTab: DevToolsTab
  setActiveTab: (tab: DevToolsTab) => void

  // Bottom panel tab (for DevToolsBottomPanel)
  activeBottomTab: DevToolsBottomTab
  setActiveBottomTab: (tab: DevToolsBottomTab) => void

  // Event log (ring buffer, max 200)
  eventLog: EventLogEntry[]
  pushEvent: (entry: Omit<EventLogEntry, 'id' | 'timestamp'>) => void
  clearEventLog: () => void

  // Flow log (ring buffer, max 100)
  flowLog: FlowLogEntry[]
  pushFlowLog: (entry: Omit<FlowLogEntry, 'id' | 'timestamp'>) => void
  clearFlowLog: () => void

  // Live state snapshot
  stateSnapshot: Record<string, unknown>
  refreshState: () => void

  // Network log (ring buffer, max 200)
  networkLog: NetworkEntry[]
  pushNetwork: (entry: Omit<NetworkEntry, 'id' | 'timestamp'>) => void
  clearNetworkLog: () => void

  // Console log (unified ring buffer, max 500)
  consoleLog: ConsoleEntry[]
  pushConsole: (entry: Omit<ConsoleEntry, 'id' | 'timestamp'>) => void
  clearConsoleLog: () => void

  // KG repository call log (ring buffer, max 500)
  kgCalls: KgCallRecord[]
  kgCallCount: number
  pushKgCall: (entry: Omit<KgCallRecord, 'id' | 'timestamp'>) => void
  clearKgCalls: () => void

  // Performance counters
  eventCount: number
  flowCount: number
  totalFlowDuration: number

  // PostMessage bridge (iframe -> host DevTools)
  /** Connect DevTools to iframe runtime via postMessage bridge events */
  bridgeFromIframe: (message: IframeMessage) => void
  /** Disconnect from iframe bridge */
  disconnectBridge: () => void
}

const MAX_EVENT_LOG = 200
const MAX_FLOW_LOG = 100
const MAX_NETWORK_LOG = 200
const MAX_CONSOLE_LOG = 500
const MAX_KG_CALLS = 500

let nextId = 1

// Stored cleanup functions for monkey-patches
let cleanupFns: Array<() => void> = []

function wireRuntime(ctx: V2RuntimeCtx, _store: ReturnType<typeof useDevToolsStore.getState>) {
  const { stateManager, actionEngine } = ctx

  // 1. Monkey-patch actionEngine.executeFlow to log flow executions + duration
  const origExecuteFlow = actionEngine.executeFlow.bind(actionEngine)
  actionEngine.executeFlow = async (flow: Flow, context?: Record<string, unknown>) => {
    const start = performance.now()
    let success = true
    let error: string | undefined
    try {
      await origExecuteFlow(flow, context)
    } catch (err) {
      success = false
      error = err instanceof Error ? err.message : String(err)
      throw err
    } finally {
      const duration = performance.now() - start
      useDevToolsStore.getState().pushFlowLog({
        flowName: flow.name ?? 'unnamed',
        duration: Math.round(duration * 100) / 100,
        stepCount: flow.steps?.length ?? 0,
        success,
        error,
      })
    }
  }
  cleanupFns.push(() => {
    actionEngine.executeFlow = origExecuteFlow
  })

  // 2. Subscribe to state changes for live snapshot
  const unsub = stateManager.subscribe('', () => {
    useDevToolsStore.getState().refreshState()
  })
  cleanupFns.push(unsub)

  // Take initial snapshot
  useDevToolsStore.getState().refreshState()
}

function unwireRuntime() {
  for (const fn of cleanupFns) {
    try { fn() } catch { /* ignore */ }
  }
  cleanupFns = []
}

export const useDevToolsStore = create<DevToolsState>()((set, get) => ({
  runtime: null,
  setRuntime: (runtime) => {
    // Cleanup previous wiring
    unwireRuntime()

    set({ runtime, stateSnapshot: {}, eventCount: 0, flowCount: 0, totalFlowDuration: 0 })

    if (runtime) {
      wireRuntime(runtime, get())
    }
  },

  activeTab: 'state',
  setActiveTab: (tab) => set({ activeTab: tab }),

  activeBottomTab: 'console',
  setActiveBottomTab: (tab) => set({ activeBottomTab: tab }),

  eventLog: [],
  pushEvent: (entry) => {
    set((state) => {
      const full: EventLogEntry = {
        ...entry,
        id: String(nextId++),
        timestamp: Date.now(),
      }
      const log = [...state.eventLog, full]
      return {
        eventLog: log.length > MAX_EVENT_LOG ? log.slice(log.length - MAX_EVENT_LOG) : log,
        eventCount: state.eventCount + 1,
      }
    })
    // Also push to unified console
    useDevToolsStore.getState().pushConsole({
      level: 'info',
      source: 'event',
      message: `Event ${entry.eventType} on ${entry.elementId}${entry.flowName ? ' -> ' + entry.flowName : ''}`,
    })
  },
  clearEventLog: () => set({ eventLog: [] }),

  flowLog: [],
  pushFlowLog: (entry) => {
    set((state) => {
      const full: FlowLogEntry = {
        ...entry,
        id: String(nextId++),
        timestamp: Date.now(),
      }
      const log = [...state.flowLog, full]
      return {
        flowLog: log.length > MAX_FLOW_LOG ? log.slice(log.length - MAX_FLOW_LOG) : log,
        flowCount: state.flowCount + 1,
        totalFlowDuration: state.totalFlowDuration + entry.duration,
      }
    })
    // Also push to unified console
    useDevToolsStore.getState().pushConsole({
      level: entry.success ? 'info' : 'error',
      source: 'flow',
      message: `Flow "${entry.flowName}" ${entry.success ? 'completed' : 'failed'} (${entry.duration}ms, ${entry.stepCount} steps)`,
      detail: entry.error || undefined,
    })
  },
  clearFlowLog: () => set({ flowLog: [] }),

  networkLog: [],
  pushNetwork: (entry) => {
    set((state) => {
      const full: NetworkEntry = {
        ...entry,
        id: String(nextId++),
        timestamp: Date.now(),
      }
      const log = [...state.networkLog, full]
      return {
        networkLog: log.length > MAX_NETWORK_LOG ? log.slice(log.length - MAX_NETWORK_LOG) : log,
      }
    })
  },
  clearNetworkLog: () => set({ networkLog: [] }),

  consoleLog: [],
  pushConsole: (entry) =>
    set((state) => {
      const full: ConsoleEntry = {
        ...entry,
        id: String(nextId++),
        timestamp: Date.now(),
      }
      const log = [...state.consoleLog, full]
      return {
        consoleLog: log.length > MAX_CONSOLE_LOG ? log.slice(log.length - MAX_CONSOLE_LOG) : log,
      }
    }),
  clearConsoleLog: () => set({ consoleLog: [] }),

  kgCalls: [],
  kgCallCount: 0,
  pushKgCall: (entry) =>
    set((state) => {
      const full: KgCallRecord = {
        ...entry,
        id: String(nextId++),
        timestamp: Date.now(),
      }
      const log = [...state.kgCalls, full]
      return {
        kgCalls: log.length > MAX_KG_CALLS ? log.slice(log.length - MAX_KG_CALLS) : log,
        kgCallCount: state.kgCallCount + 1,
      }
    }),
  clearKgCalls: () => set({ kgCalls: [], kgCallCount: 0 }),

  stateSnapshot: {},
  refreshState: () => {
    const rt = get().runtime
    if (!rt) return
    const snapshot = rt.stateManager.getSnapshot()
    set({ stateSnapshot: snapshot })
  },

  eventCount: 0,
  flowCount: 0,
  totalFlowDuration: 0,

  bridgeFromIframe: (message) => {
    const { type, payload } = message
    switch (type) {
      case IFRAME_EVENTS.STATE_SNAPSHOT: {
        const p = payload as unknown as StateSnapshotPayload
        const snapshot = p?.snapshot ?? {}
        // Set runtime to a sentinel value to indicate "live via bridge"
        // (runtime !== null triggers the green "Live" indicator in DevToolsInspector)
        const state = useDevToolsStore.getState()
        if (!state.runtime) {
          set({
            runtime: { stateManager: null as any, actionEngine: null as any },
            stateSnapshot: snapshot,
          })
        } else {
          set({ stateSnapshot: snapshot })
        }
        // Push to unified console
        useDevToolsStore.getState().pushConsole({
          level: 'info',
          source: 'state',
          message: `State updated (${Object.keys(snapshot).length} keys)`,
        })
        break
      }
      case IFRAME_EVENTS.FLOW_EXECUTED: {
        const p = payload as unknown as FlowExecutedPayload
        useDevToolsStore.getState().pushFlowLog({
          flowName: p.flowName,
          duration: p.duration,
          stepCount: p.stepCount,
          success: p.success,
          error: p.error,
        })
        break
      }
      case IFRAME_EVENTS.EVENT_FIRED: {
        const p = payload as unknown as EventFiredPayload
        useDevToolsStore.getState().pushEvent({
          elementId: p.elementId,
          eventType: p.eventType,
          flowName: p.flowName,
        })
        break
      }
      case IFRAME_EVENTS.NETWORK_REQUEST: {
        const p = payload as unknown as NetworkRequestPayload
        useDevToolsStore.getState().pushNetwork({
          method: p.method,
          url: p.url,
          status: p.status,
          duration: p.duration,
          requestBody: p.requestBody,
          responseBody: p.responseBody,
          error: p.error,
        })
        // Also push to console
        const level: ConsoleLevel = p.status >= 400 ? 'error' : 'info'
        useDevToolsStore.getState().pushConsole({
          level,
          source: 'network',
          message: `${p.method} ${p.url} ${p.status} (${p.duration}ms)`,
        })
        break
      }
      // Ignore other message types
    }
  },

  disconnectBridge: () => {
    unwireRuntime()
    set({ runtime: null, stateSnapshot: {} })
  },
}))
